import React, { useState, useEffect, useRef } from 'react';
import {
  Edit2,
  Plus,
  Trash2,
  X,
  Upload,
  Loader,
  Download,
  MoreVertical,
} from 'lucide-react';
import { ImageGenService as UpscaleService } from 'features/upscale-image/services/image-gen.api';
import toast from 'react-hot-toast';
import * as Dialog from '@radix-ui/react-dialog';
import axiosInstance from 'api/axios';
import { useNavigate } from 'react-router-dom';

// Interfaces
interface ImageItem {
  id: number;
  title: string;
  date: string;
  imageUrl: string;
}

// Upload Dialog Component
const UploadDialog = ({
  isOpen,
  onClose,
  featureType,
  onImageUpload,
}: {
  isOpen: boolean;
  onClose: () => void;
  featureType: 'upscale' | 'remover' | 'replacer';
  onImageUpload: (image: File, replacementPrompt?: string) => Promise<void>;
}) => {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [replacementPrompt, setReplacementPrompt] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedImage) {
      toast.error('Please select an image first');
      return;
    }

    setIsUploading(true);
    try {
      await onImageUpload(
        selectedImage,
        featureType === 'replacer' ? replacementPrompt : undefined
      );
      setSelectedImage(null);
      setPreviewUrl(null);
      setReplacementPrompt('');
      onClose();
      toast.success(`Image ${getFeatureActionText(featureType)} successfully!`);
    } catch (error) {
      console.error(`Error ${getFeatureActionText(featureType)} image:`, error);
      toast.error(
        `Failed to ${getFeatureActionText(featureType).toLowerCase()} image. Please try again.`
      );
    } finally {
      setIsUploading(false);
    }
  };

  const getFeatureActionText = (type: string): string => {
    switch (type) {
      case 'upscale':
        return 'Upscaled';
      case 'remover':
        return 'Processed for removal';
      case 'replacer':
        return 'Processed for replacement';
      default:
        return 'Processed';
    }
  };

  const getFeatureTitle = (type: string): string => {
    switch (type) {
      case 'upscale':
        return 'Upscale Image';
      case 'remover':
        return 'Remove Object from Image';
      case 'replacer':
        return 'Replace Object in Image';
      default:
        return 'Edit Image';
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-background p-6 rounded-lg shadow-lg max-w-md w-full">
          <div className="flex justify-between items-center mb-4">
            <Dialog.Title className="text-lg font-semibold text-text-primary">
              {getFeatureTitle(featureType)}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          <div className="space-y-4">
            {!previewUrl ? (
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  Click to upload or drag and drop an image
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PNG, JPG or JPEG (max. 10MB)
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={handleFileChange}
                />
              </div>
            ) : (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-auto rounded-lg object-contain max-h-64"
                />
                <button
                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                  onClick={() => {
                    setSelectedImage(null);
                    setPreviewUrl(null);
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {featureType === 'replacer' && previewUrl && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  What would you like to replace with?
                </label>
                <input
                  type="text"
                  value={replacementPrompt}
                  onChange={(e) => setReplacementPrompt(e.target.value)}
                  placeholder="e.g., 'a cat', 'mountains', 'red car'"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                onClick={onClose}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                onClick={handleUpload}
                disabled={
                  !selectedImage ||
                  isUploading ||
                  (featureType === 'replacer' && !replacementPrompt)
                }
              >
                {isUploading ? (
                  <>
                    <Loader size={16} className="animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  `Upload & ${getFeatureActionText(featureType)}`
                )}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

// Edit Image Modal Component
const EditImageModal = ({
  isOpen,
  onClose,
  image,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  image: ImageItem | null;
  onSave: (id: number, newName: string) => Promise<void>;
}) => {
  const [newName, setNewName] = useState(image?.title || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setNewName(image?.title || '');
  }, [image]);

  const handleSave = async () => {
    if (!image || !newName.trim()) return;

    setIsSaving(true);
    try {
      await onSave(image.id, newName.trim());
      onClose();
    } catch (error) {
      console.error('Error saving image name:', error);
      toast.error('Failed to save image name');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-background p-6 rounded-lg shadow-lg max-w-md w-full">
          <div className="flex justify-between items-center mb-4">
            <Dialog.Title className="text-lg font-semibold text-text-primary">
              Edit Image Name
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Image Name
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter image name"
              />
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                onClick={handleSave}
                disabled={!newName.trim() || isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader size={16} className="animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

// Mobile Menu Component
const Menu = ({
  item,
  onEdit,
  onDelete,
  onDownload,
}: {
  item: ImageItem;
  onEdit: (image: ImageItem) => void;
  onDelete: (id: number) => void;
  onDownload: (id: number) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleMenu();
        }}
        className="p-2 hover:bg-gray-100 rounded-full"
      >
        <MoreVertical className="text-primary h-6 w-6" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed z-30 inset-0 bg-black/20"
            onClick={toggleMenu}
          />
          <div className="fixed z-50 bottom-0 left-0 right-0 bg-background rounded-t-xl shadow-lg">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto my-3" />
            <div className="p-4 space-y-4">
              <button
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
                onClick={() => {
                  onEdit(item);
                  setIsOpen(false);
                }}
              >
                <Edit2 className="text-primary h-5 w-5" />
                <span>Edit Name</span>
              </button>
              <button
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
                onClick={() => {
                  onDownload(item.id);
                  setIsOpen(false);
                }}
              >
                <Download className="text-primary h-5 w-5" />
                <span>Download</span>
              </button>
              <button
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg text-red-600"
                onClick={() => {
                  onDelete(item.id);
                  setIsOpen(false);
                }}
              >
                <Trash2 className="h-5 w-5" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

// Common ImageCard component to be used by all sections
const ImageCard = ({
  image,
  onEdit,
  onDelete,
  onDownload,
}: {
  image: ImageItem;
  onEdit: (image: ImageItem) => void;
  onDelete: (id: number) => void;
  onDownload: (id: number) => void;
}) => {
  return (
    <div className="bg-background rounded-lg p-3 sm:p-4">
      <div className="relative group">
        <img
          src={image.imageUrl}
          alt={image.title}
          className="w-full aspect-square rounded-md object-contain mb-2 sm:mb-3"
        />
        {/* Desktop Actions */}
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 items-center justify-center space-x-3 sm:space-x-4 hidden sm:flex">
          <button
            className="p-1.5 sm:p-2 text-white hover:bg-white/20 rounded-full transition-colors"
            onClick={() => onEdit(image)}
          >
            <Edit2 size={18} />
          </button>
          <button
            className="p-1.5 sm:p-2 text-white hover:bg-white/20 rounded-full transition-colors"
            onClick={() => onDownload(image.id)}
          >
            <Download size={18} />
          </button>
          <button
            className="p-1.5 sm:p-2 text-white hover:bg-white/20 rounded-full transition-colors"
            onClick={() => onDelete(image.id)}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {/* Title, Date and Mobile Menu */}
      <div className="px-2 flex justify-between items-center">
        <div>
          <h3 className="text-[10px] sm:text-lg font-medium truncate">
            {image.title || 'Untitled Image'}
          </h3>
          <p className=" text-[10px] sm:text-lg">{image.date}</p>
        </div>
        <div className="flex items-center lg:hidden">
          <Menu
            item={image}
            onEdit={onEdit}
            onDelete={onDelete}
            onDownload={onDownload}
          />
        </div>
      </div>
    </div>
  );
};

// Update all content components to use the common ImageCard
const UpscaleContent = ({
  images,
  onDeleteImage,
  onEditImage,
  onDownloadImage,
}: {
  images: ImageItem[];
  onDeleteImage: (id: number) => void;
  onEditImage: (image: ImageItem) => void;
  onDownloadImage: (id: number) => void;
}) => {
  if (images.length === 0) {
    return (
      <div className="bg-background text-text-primary rounded-lg p-8 sm:p-16 flex flex-col items-center justify-center">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-gray-300 flex items-center justify-center mb-3 sm:mb-4">
          ⊘
        </div>
        <p className="text-sm sm:text-base text-center">
          There are no upscaled images to show right now
        </p>
      </div>
    );
  }

  return (
    <div className="grid w-full grid-cols-2 sm:grid-cols-2 text-text-primary lg:grid-cols-7 xl:grid-cols-4 gap-4 sm:gap-6">
      {images.map((image) => (
        <ImageCard
          key={image.id}
          image={image}
          onEdit={onEditImage}
          onDelete={onDeleteImage}
          onDownload={onDownloadImage}
        />
      ))}
    </div>
  );
};

const RemoverContent = ({
  images,
  onDeleteImage,
  onEditImage,
  onDownloadImage,
}: {
  images: ImageItem[];
  onDeleteImage: (id: number) => void;
  onEditImage: (image: ImageItem) => void;
  onDownloadImage: (id: number) => void;
}) => {
  if (images.length === 0) {
    return (
      <div className="bg-background text-text-primary rounded-lg p-8 sm:p-16 flex flex-col items-center justify-center">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-gray-300 flex items-center justify-center mb-3 sm:mb-4">
          ⊘
        </div>
        <p className="text-sm sm:text-base text-center">
          There are no object removal images to show right now
        </p>
      </div>
    );
  }

  return (
    <div className="grid w-full grid-cols-2 sm:grid-cols-2 text-text-primary lg:grid-cols-7 xl:grid-cols-4 gap-4 sm:gap-6">
      {images.map((image) => (
        <ImageCard
          key={image.id}
          image={image}
          onEdit={onEditImage}
          onDelete={onDeleteImage}
          onDownload={onDownloadImage}
        />
      ))}
    </div>
  );
};

const ReplacerContent = ({
  images,
  onDeleteImage,
  onEditImage,
  onDownloadImage,
}: {
  images: ImageItem[];
  onDeleteImage: (id: number) => void;
  onEditImage: (image: ImageItem) => void;
  onDownloadImage: (id: number) => void;
}) => {
  if (images.length === 0) {
    return (
      <div className="bg-background text-text-primary rounded-lg p-8 sm:p-16 flex flex-col items-center justify-center">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-gray-300 flex items-center justify-center mb-3 sm:mb-4">
          ⊘
        </div>
        <p className="text-sm sm:text-base text-center">
          There are no object replacement images to show right now
        </p>
      </div>
    );
  }

  return (
    <div className="grid w-full grid-cols-2 sm:grid-cols-2 text-text-primary lg:grid-cols-7 xl:grid-cols-4 gap-4 sm:gap-6">
      {images.map((image) => (
        <ImageCard
          key={image.id}
          image={image}
          onEdit={onEditImage}
          onDelete={onDeleteImage}
          onDownload={onDownloadImage}
        />
      ))}
    </div>
  );
};

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  };
  return date.toLocaleDateString('en-US', options).toUpperCase();
};

// Helper utility to extract image array from various nested response structures
const extractImagesFromResponse = (responseData: any): any[] => {
  console.log('Extracting images from:', responseData);

  // Direct check for the specific data structure seen in the screenshot
  if (
    responseData &&
    responseData.data &&
    typeof responseData.data === 'object'
  ) {
    // Check if data is an object with numbered keys (like 0, 1, 2...)
    if (Object.keys(responseData.data).some((key) => !isNaN(Number(key)))) {
      console.log('Found data with numbered keys - converting to array');
      const dataArray = Object.values(responseData.data);
      if (dataArray.length > 0) {
        console.log(
          'Successfully extracted',
          dataArray.length,
          'images from data object'
        );
        return dataArray;
      }
    }
  }

  // If it's already an array, validate it contains objects with image properties
  if (Array.isArray(responseData)) {
    if (
      responseData.length > 0 &&
      typeof responseData[0] === 'object' &&
      responseData[0] !== null
    ) {
      const firstItem = responseData[0] as Record<string, any>;
      if (
        firstItem.img_url ||
        firstItem.url ||
        firstItem.image_url ||
        firstItem.imageUrl
      ) {
        console.log('Response is already an array with image objects');
        return responseData;
      }
    }
  }

  // Check common paths where images might be stored
  const possiblePaths = [
    'data',
    'images',
    'results',
    'items',
    'data.images',
    'data.results',
    'data.items',
  ];

  // Try each path
  for (const path of possiblePaths) {
    const parts = path.split('.');
    let current: any = responseData;

    // Navigate through the path
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        current = null;
        break;
      }
    }

    // If we found an array at this path, check if it looks like image data
    if (
      Array.isArray(current) &&
      current.length > 0 &&
      typeof current[0] === 'object' &&
      current[0] !== null
    ) {
      const firstItem = current[0] as Record<string, any>;
      if (
        firstItem.img_url ||
        firstItem.url ||
        firstItem.image_url ||
        firstItem.imageUrl
      ) {
        console.log(`Found image array at path: ${path}`);
        return current;
      }
    }

    // If we found an object that might be a map of images
    if (current && typeof current === 'object' && !Array.isArray(current)) {
      const values = Object.values(current);
      if (
        values.length > 0 &&
        typeof values[0] === 'object' &&
        values[0] !== null
      ) {
        const firstValue = values[0] as Record<string, any>;
        if (
          firstValue.img_url ||
          firstValue.url ||
          firstValue.image_url ||
          firstValue.imageUrl
        ) {
          console.log(`Found image object map at path: ${path}`);
          return values;
        }
      }
    }
  }

  // Last resort - try to do a deep scan for arrays or objects that look like image data
  const findImageArrays = (obj: any, path = ''): any[] => {
    if (!obj || typeof obj !== 'object') return [];

    // If it's an array with image-like objects
    if (
      Array.isArray(obj) &&
      obj.length > 0 &&
      typeof obj[0] === 'object' &&
      obj[0] !== null
    ) {
      const firstItem = obj[0] as Record<string, any>;
      if (
        firstItem.img_url ||
        firstItem.url ||
        firstItem.image_url ||
        firstItem.imageUrl
      ) {
        console.log(`Deep scan found image array at: ${path}`);
        return obj;
      }
    }

    // If it's an object, check if it's a map of images
    if (!Array.isArray(obj)) {
      const values = Object.values(obj);
      if (
        values.length > 0 &&
        typeof values[0] === 'object' &&
        values[0] !== null
      ) {
        const firstValue = values[0] as Record<string, any>;
        if (
          firstValue.img_url ||
          firstValue.url ||
          firstValue.image_url ||
          firstValue.imageUrl
        ) {
          console.log(`Deep scan found image map at: ${path}`);
          return values;
        }
      }

      // Recursively search nested objects
      for (const key in obj) {
        if (obj[key] && typeof obj[key] === 'object') {
          const result = findImageArrays(obj[key], `${path}.${key}`);
          if (result.length > 0) return result;
        }
      }
    }

    return [];
  };

  const deepScanResult = findImageArrays(responseData);
  if (deepScanResult.length > 0) {
    return deepScanResult;
  }

  console.warn('Could not extract images from response data');
  console.log(
    'Dumping full response data for analysis:',
    JSON.stringify(responseData, null, 2)
  );
  return [];
};

// Convert API response to UI format
const mapApiToUiFormat = (apiImages: any[]): ImageItem[] => {
  if (!apiImages || !Array.isArray(apiImages)) {
    console.error('Invalid API response format:', apiImages);
    return [];
  }

  return apiImages.map((img) => {
    // Log the first image to see its structure
    if (apiImages.indexOf(img) === 0) {
      console.log('Sample image structure:', img);
    }

    // Try to extract ID - handle different possible field names
    const id =
      img.id || img.image_id || img._id || Math.floor(Math.random() * 10000);

    // Try to extract title - handle different possible field names
    const title =
      img.prompt_input ||
      img.prompt ||
      img.title ||
      img.name ||
      img.tool_type ||
      'Untitled Image';

    // Try to extract date - handle different possible field names
    const dateStr =
      img.created_at || img.createdAt || img.date || new Date().toISOString();
    const date = formatDate(dateStr);

    // Try to extract image URL - handle different possible field names
    const imageUrl =
      img.img_url || img.url || img.image_url || img.imageUrl || '';

    return {
      id,
      title,
      date,
      imageUrl,
    };
  });
};

// Main Gallery Component
const ImageEditTools = () => {
  const [activeTab, setActiveTab] = useState('upscale');
  const [upscaleImages, setUpscaleImages] = useState<ImageItem[]>([]);
  const [removerImages, setRemoverImages] = useState<ImageItem[]>([]);
  const [replacerImages, setReplacerImages] = useState<ImageItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState<ImageItem | null>(null);
  const navigate = useNavigate();

  const fetchImagesByType = async (toolType: string) => {
    try {
      // Using the new endpoint with tool_type parameter
      console.log(`Fetching images for tool type: ${toolType}`);

      const response = await axiosInstance.private.get(
        `/api/v1/gen-ai/get-user-images`,
        {
          params: {
            tool_type: toolType,
          },
        }
      );

      // Deep debug - show the full response structure
      console.log(
        `Full response structure for ${toolType}:`,
        JSON.stringify(response.data, null, 2)
      );

      if (response.data && response.data.success) {
        // Use the utility function to extract images from the response
        const imageData = extractImagesFromResponse(response.data);

        console.log(`Found ${imageData.length} images for ${toolType}`);

        if (imageData.length === 0) {
          console.warn(
            `No images found for ${toolType} after parsing response`
          );
        }

        const mappedImages = mapApiToUiFormat(imageData);
        console.log(`Mapped images for ${toolType}:`, mappedImages);
        return mappedImages;
      } else {
        console.error(`Error in response for ${toolType}:`, response.data);
        throw new Error(response.data?.message || 'Failed to fetch images');
      }
    } catch (error) {
      console.error(`Error fetching ${toolType} images:`, error);
      toast.error(`Failed to load ${toolType} images`);
      return [];
    }
  };

  useEffect(() => {
    const fetchAllImages = async () => {
      setIsLoading(true);
      console.log('-------------- STARTING IMAGE FETCHING --------------');
      try {
        // Fetch images by their specific tool types
        console.log('Making API calls for each tool type...');

        const upscaleData = await fetchImagesByType('UPSCALE');
        console.log('UPSCALE data fetched:', upscaleData.length, 'images');

        const removerData = await fetchImagesByType('OBJECT_REMOVAL');
        console.log(
          'OBJECT_REMOVAL data fetched:',
          removerData.length,
          'images'
        );

        const replacerData = await fetchImagesByType('SEARCH_REPLACE');
        console.log(
          'SEARCH_REPLACE data fetched:',
          replacerData.length,
          'images'
        );

        console.log('API calls complete. Updating state with received data...');

        setUpscaleImages(upscaleData);
        setRemoverImages(removerData);
        setReplacerImages(replacerData);

        console.log('-------------- IMAGE FETCHING COMPLETE --------------');
      } catch (error) {
        console.error('Error fetching images:', error);
        toast.error('Failed to load images. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllImages();
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    // No need to re-fetch as we already have all images loaded
  };

  const handleDeleteImage = async (id: number, imageType: string) => {
    try {
      const response = await UpscaleService.deleteImage(id);

      if (response.success) {
        // Refresh the appropriate image list based on the current image type
        let updatedImages: ImageItem[] = [];

        switch (imageType) {
          case 'upscale':
            updatedImages = await fetchImagesByType('UPSCALE');
            setUpscaleImages(updatedImages);
            break;
          case 'remover':
            updatedImages = await fetchImagesByType('OBJECT_REMOVAL');
            setRemoverImages(updatedImages);
            break;
          case 'replacer':
            updatedImages = await fetchImagesByType('SEARCH_REPLACE');
            setReplacerImages(updatedImages);
            break;
        }

        toast.success('Image deleted successfully');
      } else {
        toast.error('Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleImageUpload = async (image: File, replacementPrompt?: string) => {
    const formData = new FormData();
    formData.append('image', image);

    if (replacementPrompt) {
      formData.append('replacement_text', replacementPrompt);
    }

    try {
      // Use the appropriate service based on the active tab
      let response;
      const token = localStorage.getItem('token') || '';

      switch (activeTab) {
        case 'upscale':
          response = await UpscaleService.upscaleImage(formData, token);
          if (response.success) {
            // Refetch upscale images to get the updated list
            const updatedImages = await fetchImagesByType('UPSCALE');
            setUpscaleImages(updatedImages);
          }
          break;
        case 'remover':
          response = await UpscaleService.removeObject(formData, token);
          if (response.success) {
            // Refetch remover images to get the updated list
            const updatedImages = await fetchImagesByType('OBJECT_REMOVAL');
            setRemoverImages(updatedImages);
          }
          break;
        case 'replacer':
          response = await UpscaleService.replaceObject(formData, token);
          if (response.success) {
            // Refetch replacer images to get the updated list
            const updatedImages = await fetchImagesByType('SEARCH_REPLACE');
            setReplacerImages(updatedImages);
          }
          break;
      }

      if (!response || !response.success) {
        throw new Error('Failed to process image');
      }
    } catch (error) {
      console.error('Error processing image:', error);
      throw error;
    }
  };

  const handleEditImage = (image: ImageItem) => {
    setEditingImage(image);
  };

  const handleSaveImageName = async (imageId: number, newName: string) => {
    try {
      const response = await UpscaleService.editImageName(imageId, {
        name: newName,
      });

      if (response.success) {
        // Refresh the appropriate image list
        const updatedImages = await fetchImagesByType(
          activeTab === 'upscale'
            ? 'UPSCALE'
            : activeTab === 'remover'
              ? 'OBJECT_REMOVAL'
              : 'SEARCH_REPLACE'
        );

        switch (activeTab) {
          case 'upscale':
            setUpscaleImages(updatedImages);
            break;
          case 'remover':
            setRemoverImages(updatedImages);
            break;
          case 'replacer':
            setReplacerImages(updatedImages);
            break;
        }

        toast.success('Image name updated successfully');
      } else {
        toast.error('Failed to update image name');
      }
    } catch (error) {
      console.error('Error updating image name:', error);
      toast.error('Failed to update image name');
    }
  };

  const handleDownloadImage = async (id: number) => {
    try {
      const blob = await UpscaleService.downloadImage(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'image.png'; // You can set a dynamic name based on the image title
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Image downloaded successfully');
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to download image');
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="bg-background text-text-primary rounded-lg p-8 sm:p-16 flex flex-col items-center justify-center">
          <Loader size={30} className="animate-spin mb-4" />
          <p className="text-sm sm:text-base text-center">Loading images...</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'upscale':
        return (
          <UpscaleContent
            images={upscaleImages}
            onDeleteImage={(id) => handleDeleteImage(id, 'upscale')}
            onEditImage={handleEditImage}
            onDownloadImage={handleDownloadImage}
          />
        );
      case 'remover':
        return (
          <RemoverContent
            images={removerImages}
            onDeleteImage={(id) => handleDeleteImage(id, 'remover')}
            onEditImage={handleEditImage}
            onDownloadImage={handleDownloadImage}
          />
        );
      case 'replacer':
        return (
          <ReplacerContent
            images={replacerImages}
            onDeleteImage={(id) => handleDeleteImage(id, 'replacer')}
            onEditImage={handleEditImage}
            onDownloadImage={handleDownloadImage}
          />
        );
      default:
        return (
          <UpscaleContent
            images={upscaleImages}
            onDeleteImage={(id) => handleDeleteImage(id, 'upscale')}
            onEditImage={handleEditImage}
            onDownloadImage={handleDownloadImage}
          />
        );
    }
  };

  const handleNewImageEdit = () => {
    switch (activeTab) {
      case 'upscale':
        navigate('/upscale-image/get-started');
        break;
      case 'remover':
        navigate('/object-remover/get-started');
        break;
      case 'replacer':
        navigate('/object-replacer/get-started');
        break;
      default:
        navigate('/upscale-image/get-started');
    }
  };

  return (
    <div className=" mx-auto rounded-lg px-4 text-text-primary sm:px-6 lg:px-8 py-8 w-full max-w-[1400px]">
      {/* Header Section */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-semibold mb-2">Your Images</h1>
        <p className="text-sm sm:text-base ">
          All your images are safely stored here in one place. Easily view,
          organize, and manage your them here.
        </p>
      </div>

      {/* Tabs */}
      <div className="hidden sm:flex flex-col sm:flex-row justify-between gap-4 sm:gap-0 mb-6 sm:mb-8">
        <div className="flex flex-wrap ">
          <button
            onClick={() => handleTabChange('upscale')}
            className={`px-3 sm:px-4 py-2 text-sm sm:text-base transition-colors  ${
              activeTab === 'upscale'
                ? 'bg-primary text-white'
                : 'bg-secondary text-primary'
            }`}
          >
            Upscale Image
          </button>
          <button
            onClick={() => handleTabChange('remover')}
            className={`px-3 sm:px-4 py-2 text-sm sm:text-base transition-colors ${
              activeTab === 'remover'
                ? 'bg-primary text-white'
                : 'bg-secondary text-primary'
            }`}
          >
            Image Remover
          </button>
          <button
            onClick={() => handleTabChange('replacer')}
            className={`px-3 sm:px-4 py-2 text-sm sm:text-base transition-colors  ${
              activeTab === 'replacer'
                ? 'bg-primary text-white'
                : 'bg-secondary text-primary'
            }`}
          >
            Image Replacer
          </button>
        </div>
        <button
          className="hidden lg:flex items-center justify-center text-primary cursor-pointer text-sm sm:text-base"
          onClick={handleNewImageEdit}
        >
          <Plus size={20} className="sm:size-22" />
          <span className=" underline ml-1">Edit new image</span>
        </button>
      </div>

      {/* Mobile Tabs */}
      <div className="sm:hidden flex w-full border-b border-gray-200 mb-6">
        <button
          onClick={() => handleTabChange('upscale')}
          className={`flex-1 py-3 text-center text-sm font-medium ${
            activeTab === 'upscale'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500'
          }`}
        >
          Upscale Image
        </button>
        <button
          onClick={() => handleTabChange('remover')}
          className={`flex-1 py-3 text-center text-sm font-medium ${
            activeTab === 'remover'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500'
          }`}
        >
          Image Remover
        </button>
        <button
          onClick={() => handleTabChange('replacer')}
          className={`flex-1 py-3 text-center text-sm font-medium ${
            activeTab === 'replacer'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500'
          }`}
        >
          Image Replacer
        </button>
      </div>

      {/* Floating Action Button (Mobile) */}
      <button
        onClick={handleNewImageEdit}
        className="lg:hidden fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-lg z-10"
      >
        <Plus size={24} />
      </button>

      {/* Content Area */}
      {renderContent()}

      {/* Upload Dialog */}
      <UploadDialog
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        featureType={activeTab as 'upscale' | 'remover' | 'replacer'}
        onImageUpload={handleImageUpload}
      />

      {/* Edit Image Modal */}
      <EditImageModal
        isOpen={!!editingImage}
        onClose={() => setEditingImage(null)}
        image={editingImage}
        onSave={handleSaveImageName}
      />
    </div>
  );
};

export default ImageEditTools;
